// src/ui/components/ARSkeletonOverlay.tsx
import { useEffect, useRef } from "react";
import { colors } from "../design-system/colors";

// BlazePose 33点のうち、通常モードで描画する主要な骨格接続のみ(過剰な情報を避ける、2.4節)
const CONNECTIONS: [number, number][] = [
  [11, 12], // 肩-肩
  [11, 23], // 左肩-左股
  [12, 24], // 右肩-右股
  [23, 24], // 股-股
  [23, 25], // 左股-左膝
  [25, 27], // 左膝-左足首
  [24, 26], // 右股-右膝
  [26, 28], // 右膝-右足首
  [11, 13], // 左肩-左肘
  [13, 15], // 左肘-左手首
  [12, 14], // 右肩-右肘
  [14, 16], // 右肘-右手首
];

export interface OverlayKeypoints {
  flat: Float32Array; // MediaPipe正規化座標(x,y,z,visibility) x 33
  width: number;
  height: number;
}

export default function ARSkeletonOverlay({ keypoints }: { keypoints: OverlayKeypoints | null }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!keypoints) return;

    const { flat, width, height } = keypoints;
    canvas.width = width;
    canvas.height = height;

    const point = (i: number) => ({
      x: flat[i * 4] * width,
      y: flat[i * 4 + 1] * height,
      visibility: flat[i * 4 + 3],
    });

    ctx.strokeStyle = colors.amber;
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    for (const [a, b] of CONNECTIONS) {
      const pa = point(a);
      const pb = point(b);
      if (pa.visibility < 0.3 || pb.visibility < 0.3) continue;
      ctx.beginPath();
      ctx.moveTo(pa.x, pa.y);
      ctx.lineTo(pb.x, pb.y);
      ctx.stroke();
    }

    ctx.fillStyle = colors.textPrimary;
    const jointIndices = new Set(CONNECTIONS.flat());
    for (const i of jointIndices) {
      const p = point(i);
      if (p.visibility < 0.3) continue;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 5, 0, Math.PI * 2);
      ctx.fill();
    }
  }, [keypoints]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
    />
  );
}
